import SettingsApi from '../../api'
import AuthApi from 'portal-modules/auth/api'
import { call, put, takeLatest } from 'redux-saga/effects'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import createRequestSaga, {
  handleResponseError,
} from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import getProjectName from 'portal-modules/common/utils/get-project-name'
import { addSettings } from 'portal-modules/common/redux/modules/settings'
import { localStorage as local } from '@entrata/utils'
import { CUSTOM_JWT_STORAGE_KEY } from 'portal-modules/common/constants/keys'
import getProjectConditions from 'portal-modules/common/utils/get-project-conditions'
const { isRH } = getProjectConditions()

// action types
export const REQUEST_PROFILE = 'rp/profile/REQUEST_PROFILE'
export const RECEIVE_PROFILE = 'rp/profile/RECEIVE_PROFILE'
export const PROFILE_UPDATED = 'rp/profile/PROFILE_UPDATED'
export const PROFILE_IMAGE_UPLOADED = 'rp/profile/PROFILE_IMAGE_UPLOADED'
export const UPDATE_PASSWORD = 'rp/profile/UPDATE_PASSWORD'
export const VERIFY_PASSWORD = 'rp/profile/VERIFY_PASSWORD'
export const UPDATE_PROFILE = 'rp/profile/UPDATE_PROFILE'
export const UPLOAD_PROFILE_IMAGE = 'rp/profile/UPLOAD_PROFILE_IMAGE'
export const UPDATE_PROFILE_EMAIL = 'rp/profile/UPDATE_PROFILE_EMAIL'

export const primaryPhoneNumberSelector = profile =>
  profile?.phone_numbers
    ?.filter(phone => phone.is_primary === true)
    ?.map(item => item.phone_number)
    .shift()

export const primaryPhoneTypeSelector = profile =>
  profile?.phone_numbers
    ?.filter(phone => phone.is_primary === true)
    ?.map(item => item.primary_phone_type)
    .shift()

// reducer
export default (state = {}, { payload, type }) => {
  switch (type) {
    case RECEIVE_PROFILE:
    case PROFILE_UPDATED:
      return { ...state, ...payload }
    case PROFILE_IMAGE_UPLOADED:
      return {
        ...state,
        image_url: payload,
      }
    case LOGOUT:
      return {}
    default:
      return state
  }
}

// action creators

export const requestProfile = (lease_id = null) => ({
  type: REQUEST_PROFILE,
  lease_id,
})

// payload = object (changed keys only)
export const updateProfile = createFormAction(UPDATE_PROFILE)

// payload = { old_password: str, new_password: str }
export const updatePassword = createFormAction(UPDATE_PASSWORD)

// payload = { current_password: str }
export const verifyPassword = createFormAction(VERIFY_PASSWORD)

export const updateProfileEmail = ({payload, callback}) => ({
  type: UPDATE_PROFILE_EMAIL,
  callback,
  payload,
})

export const uploadProfileImage = (payload, callback) => ({
  type: UPLOAD_PROFILE_IMAGE,
  callback,
  payload,
})

const getUpdatedPhoneData = phoneData => {
  let phoneNumbers = []
  phoneNumbers = phoneData?.map(phone => {
    return {
      is_primary: phone?.is_primary,
      phone_number: phone?.phone_number,
      phone_number_type_id:
        phone?.phone_number_type_id || phone?.phone_number_type,
    }
  })
  return phoneNumbers
}
// sagas
export function* fetchProfile({ lease_id }) {
  if (getProjectName() == 'rh') {
    const profile = yield call(SettingsApi.getProfile, { lease_id })
    yield put({
      type: RECEIVE_PROFILE,
      payload: Object.entries(profile).reduce(
        (obj, [key, value]) => ({ ...obj, [key]: value || '' }),
        {},
      ),
    })
  } else {
    const {
      customer_profile,
      settings,
      pronouns = [],
    } = yield call(SettingsApi.getProfileWithSettings, { lease_id })
    yield put({
      type: RECEIVE_PROFILE,
      payload: {
        pronouns,
        ...Object.entries(customer_profile).reduce(
          (obj, [key, value]) => ({ ...obj, [key]: value || '' }),
          {},
        ),
      },
    })
    yield put(addSettings({ profile: settings }))
  }
}

export function* postPasswordUpdate({ payload }) {
  if (getProjectName() == 'tp') {
    if (payload.tenant_user_id) {
      yield call(SettingsApi.updateTenantContactPassword, payload)
    } else {
      yield call(AuthApi.updateProfilePassword, payload)
    }
  } else if (payload.group_user_id) {
    yield call(SettingsApi.updateGroupContactPassword, payload)
  } else {
    yield call(AuthApi.updatePassword, payload)
  }
}

export function* postVerifyPassword({ payload }) {
  yield call(AuthApi.verifyPassword, payload)
}
export function* postProfileUpdates({ payload, lease_id }) {
  if (payload?.isFromBulkPayment) {
    yield call(local.set, CUSTOM_JWT_STORAGE_KEY, payload?.token)
    yield call(SettingsApi.saveProfileForBulkPayment, { ...payload, lease_id })
  } else {
    yield call(SettingsApi.saveProfile, { ...payload, lease_id })
  }
  if (payload?.phone_numbers && payload.phone_numbers?.length) {
    payload.phone_numbers = getUpdatedPhoneData(payload.phone_numbers)
  }

  yield put({ type: PROFILE_UPDATED, payload })
}

export function* postProfileImage({ payload }) {
  const { success, image_path } = yield call(SettingsApi.uploadProfileImage, {
    profileImage: payload,
  })
  if (success === true) {
    yield put({ type: PROFILE_IMAGE_UPLOADED, payload: image_path })
  }
}

export function* postProfileEmailUpdate({ payload, callback }) {
  yield call(SettingsApi.updateEmail, payload)
  yield put({ type: PROFILE_UPDATED, payload: {email_address: payload.email_address} })
  typeof callback === 'function' && callback()
}

export function* watchUpdatePassword() {
  yield takeLatest(
    UPDATE_PASSWORD,
    createRequestSaga(postPasswordUpdate, {
      key: 'modal',
      message: __('Updating your password'),
      successMessage: __('Password updated successfully'),
      write: true,
    }),
  )
}
export function* watchVerifyPassword() {
  yield takeLatest(
    VERIFY_PASSWORD,
    createRequestSaga(postVerifyPassword, {
      key: 'modal',
      message: __('Verifying your password'),
      successMessage: __('Password verified successfully'),
      write: true,
    }),
  )
}

export function* watchUpdateProfile() {
  yield takeLatest(
    UPDATE_PROFILE,
    createRequestSaga(postProfileUpdates, {
      key: 'updateProfile',
      message: __('Syncing changes'),
      successMessage: __('Profile updated'),
      onError: handleProfileError,
      write: true,
    }),
  )
}

export function* handleProfileError(err) {
  yield call(handleResponseError, err, { key: 'profile' })
}

export function* watchUploadProfileImage() {
  yield takeLatest(
    UPLOAD_PROFILE_IMAGE,
    createRequestSaga(postProfileImage, {
      key: 'profile',
      message: __('Uploading your image'),
      successMessage: __('Profile updated!'),
      write: true,
    }),
  )
}

export function* watchRequestProfile() {
  yield takeLatest(
    REQUEST_PROFILE,
    createRequestSaga(fetchProfile, {
      key: 'profile',
      message: __('Looking up your profile'),
    }),
  )
}

export function* watchUpdateProfileEmail() {
  yield takeLatest(
    UPDATE_PROFILE_EMAIL,
    createRequestSaga(postProfileEmailUpdate, {
      key: 'updateProfile',
      message: __('Syncing changes'),
      successMessage: __('Email updated sucessfully'),
      onError: handleProfileError,
      write: true,
    }),
  )
}

export const sagas = [
  watchRequestProfile,
  watchUpdateProfile,
  watchUploadProfileImage,
  watchUpdatePassword,
  watchVerifyPassword,
  watchUpdateProfileEmail
]
